import { useState } from "react";
import { Bell, Plus, Server, Network, MessageCircle, Boxes } from "lucide-react";
import { useServices } from "@/lib/services";
import { PayStatus } from "@/components/avisos/PayStatus";
import { PayHistory } from "@/components/avisos/PayHistory";
import { ServiceForm } from "@/components/avisos/ServiceForm";
import { ItemActions } from "@/components/avisos/ItemActions";
import { BlinkDot } from "@/components/avisos/BlinkDot";

const GROUPS = [
  ["panel", "Painel e APIs (SYSLOG)", Server, "text-violet-300"],
  ["proxy", "Proxys", Network, "text-sky-300"],
  ["whatsapp", "WhatsApp (números virtuais)", MessageCircle, "text-emerald-300"],
  ["other", "Outros serviços", Boxes, "text-slate-300"],
];

const Row = ({ it, onEdit }) => (
  <div data-testid={`aviso-row-${it.id}`}
    className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 transition-colors ${it.status.alert ? "border-orange-500/50 bg-orange-500/[0.07]" : "border-[#1F293D] bg-[#0F141F]"}`}>
    <div className="w-full min-w-0 sm:w-64">
      <div className="flex items-center gap-2 font-semibold text-white">{it.label}
        {it.critical && <span data-testid={`aviso-critical-${it.id}`} title="Admin recebe SMS no período de aviso" className="rounded bg-red-500/15 px-1.5 text-[10px] font-bold uppercase text-red-300">Crítico · SMS</span>}</div>
      <div className="truncate text-xs text-slate-500">{[it.name, it.kind === "whatsapp" ? it.detail : "", it.note].filter(Boolean).join(" · ")}</div>
    </div>
    <div data-testid={`aviso-day-${it.id}`} className="w-40 text-xs text-slate-300">Todo dia <b className="font-mono text-white">{String(it.due_day).padStart(2, "0")}</b>
      <div className="text-[11px] text-slate-500">avisa {it.warn_before || 1} dia(s) antes</div></div>
    <div className="min-w-0 flex-1"><PayStatus it={it} /></div>
    <ItemActions it={it} onEdit={onEdit} />
  </div>
);

const Group = ({ kind, title, icon: Icon, tone, items, onEdit }) => {
  const hot = items.filter((i) => i.status.alert).length;
  return (
    <section data-testid={`avisos-group-${kind}`} className="rounded-2xl border border-[#1F293D] bg-[#111827] p-4 sm:p-5">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-200">
        <Icon className={`h-4 w-4 ${tone}`} /> {title} <span className="text-slate-500">· {items.length}</span>
        {hot > 0 && <span data-testid={`avisos-group-alerts-${kind}`} className="ml-auto flex items-center gap-1.5 text-xs normal-case text-orange-300"><BlinkDot testid={`avisos-group-dot-${kind}`} />{hot} pedindo atenção</span>}
      </h2>
      <div className="space-y-2">{items.map((it) => <Row key={it.id} it={it} onEdit={() => onEdit(it)} />)}</div>
    </section>
  );
};

export default function Avisos() {
  const { items, alerts, today } = useServices(30000);
  const [form, setForm] = useState(null);
  const due = items.filter((i) => i.due_day).sort((a, b) => Number(b.status.alert) - Number(a.status.alert) || a.due_day - b.due_day);
  return (
    <div data-testid="avisos-page" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-extrabold tracking-tight text-slate-100"><Bell className="h-7 w-7 text-orange-400" /> Avisos</h1>
          <p data-testid="avisos-summary" className="mt-1 text-sm text-slate-400">
            Vencimentos dos serviços: avisa antes (conforme cada item), no dia e 1 dia depois · {alerts ? `${alerts} pedindo atenção agora` : "nada pendente agora"}
          </p>
        </div>
        <button data-testid="avisos-add" onClick={() => setForm({})} className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-bold text-slate-950 transition-transform hover:-translate-y-0.5">
          <Plus className="h-4 w-4" /> Novo vencimento
        </button>
      </div>
      {GROUPS.map(([k, title, icon, tone]) => {
        const list = due.filter((i) => i.kind === k);
        return list.length > 0 && <Group key={k} kind={k} title={title} icon={icon} tone={tone} items={list} onEdit={setForm} />;
      })}
      {!due.length && <div className="py-10 text-center text-sm text-slate-500">Nenhum serviço com vencimento cadastrado.</div>}
      <PayHistory stamp={JSON.stringify(items.map((i) => i.status.state))} />
      <p className="text-[11px] text-slate-600">Hoje: {today?.split("-").reverse().join("/")}</p>
      <ServiceForm open={!!form} item={form?.id ? form : null} onClose={() => setForm(null)} />
    </div>
  );
}
